/**
 * The screen somebody sees while they are being moved between Tab Service and e-filing.
 *
 * It is the sign-in screen's own frame - the showcase on the left, the 400px column on the
 * right - with the column holding the shape of a form that is about to be filled, rather
 * than a white card with a spinner in the middle of an empty page. Coming across from the
 * portal now looks like arriving at the product, and when the hand-over finishes nothing
 * jumps: the app draws where the placeholder was.
 *
 * While it is working it says nothing: narrating "signing you in" tells somebody that
 * something is being done to their account between two places, which is our plumbing and
 * not their business, and it is on screen for under a second. Words appear only when it
 * fails, in the same frame, so the person is left with something they can act on.
 */
import { AuthLayout } from '@/components/auth-layout';
import { Skeleton } from '@/components/ui/feedback';
import { LOGO_URL } from '@/lib/asset';

export function HandoverScreen({
  busy = false,
  title,
  detail,
  children,
}: {
  /** Still working: the shape of what is coming, and nothing else. */
  busy?: boolean;
  /** What went wrong. Left out while it is working. */
  title?: string;
  /** One sentence under the heading. */
  detail?: string;
  /** Anything the failure needs: the reason, and the way out of it. */
  children?: React.ReactNode;
}) {
  return (
    <AuthLayout>
      <img src={LOGO_URL} alt="TAB Service Company" width={130} height={40} className="h-10 w-auto lg:hidden" />

      {title ? (
        <div>
          <h1 className="text-[28px] leading-9 font-semibold text-primary">{title}</h1>
          {detail ? <p className="mt-2 text-base text-muted-foreground">{detail}</p> : null}
        </div>
      ) : null}

      {busy ? (
        <div role="status" aria-label="Loading" className="flex flex-col gap-6" aria-busy="true">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-7 w-[140px]" />
            <Skeleton className="h-4 w-[300px] max-w-full" />
          </div>
          <div className="flex flex-col gap-4">
            {[0, 1].map((index) => (
              <div key={index} className="flex flex-col gap-1.5">
                <Skeleton className="h-3.5 w-[70px]" />
                <Skeleton className="h-10 rounded-md" />
              </div>
            ))}
            <Skeleton className="mt-1 h-10 rounded-md" />
          </div>
        </div>
      ) : null}

      {children ? <div className="flex w-full flex-col gap-4">{children}</div> : null}
    </AuthLayout>
  );
}
